import { type ReactNode, useEffect, useState } from "react";
import { StyleSheet } from "react-native-unistyles";
import { Input, SingleDatePicker } from "@visioni/a3s-ui/components";
import { View } from "@src/components";

/**
 * A time range as the URL carries it: wall clocks in the list's timezone,
 * `YYYY-MM-DD` for a whole day or `YYYY-MM-DDTHH:mm` once a time narrows it.
 * Either end may be absent, leaving the range open on that side.
 */
export interface TimeBoundValues {
  readonly from?: string | undefined;
  readonly to?: string | undefined;
}

interface TimeBoundFieldsProps {
  readonly value: TimeBoundValues;
  readonly onChange: (next: { from: string | undefined; to: string | undefined }) => void;
  /** Shown in the From date while it is empty — what an open start means for this list. */
  readonly fromPlaceholder: string;
  /** A field to lead the row with, such as the timezone the bounds are read in, so they wrap together. */
  readonly leading?: ReactNode;
}

const dayOf = (wallClock: string | undefined) => wallClock?.slice(0, 10);
const timeOf = (wallClock: string | undefined) => wallClock?.slice(11) ?? "";
const join = (day: string | undefined, time: string) =>
  day === undefined ? undefined
  : time === "" ? day
  : `${day}T${time}`;

const pad = (n: number) => String(n).padStart(2, "0");
/** The picker's calendar day, as `YYYY-MM-DD`. The picker works in local calendar dates, with no zone of their own. */
const toDay = (date: Date | null) =>
  date === null ? undefined : `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const toDate = (day: string | undefined) => {
  if (day === undefined) return null;
  const [y, m, d] = day.split("-").map(Number);
  return y === undefined || m === undefined || d === undefined ? null : new Date(y, m - 1, d);
};

const TIME = /^(\d{1,2}):(\d{2})$/;

/** A typed time as `HH:mm`, `""` for blank, or `undefined` when it is not a 24-hour time. */
function normaliseTime(text: string): string | undefined {
  const trimmed = text.trim();
  if (trimmed === "") return "";
  const match = TIME.exec(trimmed);
  if (!match) return undefined;
  const [hours, minutes] = [Number(match[1]), Number(match[2])];
  if (hours > 23 || minutes > 59) return undefined;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/**
 * A start and an end, each a date with an optional time, and each optional —
 * an absent end leaves the range open on that side. Laid out like the clip
 * compilation request's start and end, so the support tools read alike. A time
 * applies only once its date is chosen.
 */
export function TimeBoundFields({ value, onChange, fromPlaceholder, leading }: TimeBoundFieldsProps) {
  const fromDay = dayOf(value.from);
  const toDayValue = dayOf(value.to);

  return (
    <View style={styles.row}>
      {leading !== undefined && <View style={[styles.leading, styles.lead]}>{leading}</View>}
      <View style={[styles.bound, styles.first]}>
        <View style={styles.date}>
          <SingleDatePicker
            value={toDate(fromDay)}
            // Keeps a time already entered when only its date changes.
            onChange={(date) => onChange({ from: join(toDay(date), timeOf(value.from)), to: value.to })}
            maxDate={toDate(toDayValue) ?? undefined}
            label="From date"
            floatLabel="always"
            placeholder={fromPlaceholder}
            clearable
          />
        </View>
        <View style={styles.time}>
          <TimeInput
            label="From time"
            committed={timeOf(value.from)}
            disabled={fromDay === undefined}
            placeholder="Start of day"
            onCommit={(time) => onChange({ from: join(fromDay, time), to: value.to })}
          />
        </View>
      </View>
      <View style={[styles.bound, styles.second]}>
        <View style={styles.date}>
          <SingleDatePicker
            value={toDate(toDayValue)}
            onChange={(date) => onChange({ from: value.from, to: join(toDay(date), timeOf(value.to)) })}
            minDate={toDate(fromDay) ?? undefined}
            label="To date"
            floatLabel="always"
            placeholder="Up to now"
            clearable
          />
        </View>
        <View style={styles.time}>
          <TimeInput
            label="To time"
            committed={timeOf(value.to)}
            disabled={toDayValue === undefined}
            placeholder="End of day"
            onCommit={(time) => onChange({ from: value.from, to: join(toDayValue, time) })}
          />
        </View>
      </View>
    </View>
  );
}

interface TimeInputProps {
  readonly label: string;
  readonly committed: string;
  readonly disabled: boolean;
  /** Also says what a blank time means, so the field needs no help text. */
  readonly placeholder: string;
  readonly onCommit: (time: string) => void;
}

/**
 * A time typed into a draft and applied once it is a whole 24-hour time or
 * blank, so a half-typed `02:` never reaches the URL and shows as a bad link.
 */
function TimeInput({ label, committed, disabled, placeholder, onCommit }: TimeInputProps) {
  const [draft, setDraft] = useState(committed);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setDraft(committed);
  }, [committed]);

  const normalised = normaliseTime(draft);

  return (
    <Input
      label={label}
      floatLabel="always"
      value={draft}
      onChangeText={(text) => {
        setDraft(text);
        const time = normaliseTime(text);
        if (time !== undefined && time !== committed) onCommit(time);
      }}
      onBlur={() => setTouched(true)}
      disabled={disabled}
      placeholder={disabled ? "Choose a date first" : placeholder}
      error={touched && normalised === undefined ? "Use 24-hour time, e.g. 02:00" : undefined}
      clearable
      onClear={() => {
        setDraft("");
        if (committed !== "") onCommit("");
      }}
    />
  );
}

const styles = StyleSheet.create((theme) => ({
  row: { flexDirection: "row", flexWrap: "wrap", alignItems: "flex-start", gap: theme.spacing.lg },
  leading: { flexBasis: "24%", flexGrow: 1, minWidth: 220 },
  bound: { flexDirection: "row", gap: theme.spacing.md, flexBasis: "34%", flexGrow: 1, minWidth: 320 },
  date: { flexGrow: 2, flexBasis: 0, minWidth: 160 },
  time: { flexGrow: 1, flexBasis: 0, minWidth: 120 },
  // Each dropdown and calendar opens over the fields after it, so earlier fields stack higher.
  lead: { zIndex: 3 },
  first: { zIndex: 2 },
  second: { zIndex: 1 },
}));
